import React, { useEffect, useRef, useState } from 'react';
import { Button, Col, Label, ModalBody, ModalHeader, Row } from 'reactstrap';
import './SaveVideoClipPopup.scss';
import { Field, Form } from '@availity/form';
import * as yup from 'yup';
import ButtonLoader from '../../../../components/Layout/Buttons/ButtonLoader';
import Rodal from '../../../../components/Rodal/Rodal';
import { useDispatch } from 'react-redux';
import { LoadPricing } from '../../../../slices/pricing/load-pricing.thunk';
import Currency from '../../../../components/Currency/Currency';
import SaveClipPaymentComponent from '../../../../components/PaymentDialog/SaveClipPaymentComponent/SaveClipPaymentComponent';
import Helpers from '../../../../utils/helper';
import createNotification from '../../../../utils/createNotification';
import { Pricing } from '../../../../slices/pricing/pricing.model';
import { useStateSelector } from '../../../../store/selectors';

export interface VideoClip {
    id?: number;
    title?: string;
    description?: string;
    timeStartSeconds: number;
    timeEndSeconds: number;
    conferenceId: number;
    conferenceName?: string;
    conferenceDate?: Date;
    conferenceDescription?: string;
    isShared?: boolean;
    creatorId?: number;
}
interface VideoClipPopupProps {
    clip: VideoClip;
    groupId: number;
    reset: () => void;
    onClipSave: () => void;
    type?: 'group' | 'marketplace';
    isSupportMode?: boolean;
}
export const VideoClipPopup = (props: VideoClipPopupProps) => {
    const [isSaving, setIsSaving] = useState(false);
    const [clipName, setClipName] = useState('');
    const [clipDescription, setClipDescription] = useState('');
    const modalForm = useRef(null);
    const dispatch: any = useDispatch();
    const pricing: Pricing = useStateSelector((state) => state.pricing.pricing);
    const [isPaymentPopupVisible, setIsPaymentPopupVisible] = useState(false);
    const axios = useStateSelector((state) => state.core.axios);

    useEffect(() => {
        dispatch(LoadPricing());
    }, []);

    useEffect(() => {
        if (!isPaymentPopupVisible) {
            setIsSaving(false);
        }
    }, [isPaymentPopupVisible]);

    const formatRangeTimeStamp = (seconds: number): string => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const remainingSeconds = Math.floor(seconds % 60);

        if (hours === 0) {
            return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
        } else {
            return `${hours}:${minutes
                .toString()
                .padStart(2, '0')}:${remainingSeconds
                .toString()
                .padStart(2, '0')}`;
        }
    };

    const buttonText = () => {
        const isMarketplace = props.type === 'marketplace';
        return Helpers.isAdmin() || isMarketplace ? (
            <>Save Clip</>
        ) : (
            <>
                Save Clip -{' '}
                <b>
                    <Currency
                        quantity={Number(pricing.clipSave)}
                        currency="USD"
                    />
                </b>
            </>
        );
    };

    const saveClip = (videoClip: VideoClip, onComplete: () => void) => {
        if (!videoClip) onComplete();
        
        const isMarketplace = props.type === 'marketplace';
        let endpoint: string;
        
        if (isMarketplace && props.isSupportMode) {
            endpoint = `/api/Marketplace/MarketplaceMeetings/${videoClip.conferenceId}/clips`;
        } else if (isMarketplace) {
            endpoint = `/api/Marketplace/Engagements/${props.groupId}/EngagementMeetings/${videoClip.conferenceId}/clips`;
        } else {
            endpoint = `/api/groups/${props.groupId}/clips`;
        }
            
        axios
            .post(endpoint, videoClip)
            .then((response: any) => {
                if (response.status === 200) {
                    props.reset();
                    createNotification(
                        'Virtual Meeting Clip has been successfully created',
                        'success'
                    );
                } else {
                    createNotification(
                        'Error occured while creating Virtual Meeting Clip',
                        'error'
                    );
                }
            })
            .catch(() => {
                createNotification(
                    'Error occured while creating Virtual Meeting Clip',
                    'error'
                );
            })
            .finally(() => {
                onComplete();
                props.onClipSave();
            });
    };

    const paymentSuccess = () => {
        setIsSaving(false);
        setIsPaymentPopupVisible(false);
        setTimeout(() => {
            props.onClipSave();
        }, 2000);
        props.reset();
    };

    return (
        <div>
            <div className="save-video-clip-popup">
                <Rodal
                    visible={!!props.clip}
                    closeMaskOnClick={false}
                    closeOnEsc={true}
                    width={450}
                    onClose={() => {
                        props.reset();
                    }}
                    animation={'fade'}
                    showMask={true}>
                    <ModalHeader>Save Video Clip</ModalHeader>
                    <ModalBody>
                        <Row>
                            <Col md={3}>
                                <Label>Start Time:</Label>
                            </Col>
                            <Col md={9}>
                                <Label>
                                    {formatRangeTimeStamp(
                                        props.clip.timeStartSeconds
                                    )}
                                </Label>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={3}>
                                <Label>End Time:</Label>
                            </Col>
                            <Col md={9}>
                                <Label>
                                    {formatRangeTimeStamp(
                                        props.clip.timeEndSeconds
                                    )}
                                </Label>
                            </Col>
                        </Row>
                        <Form
                            innerRef={modalForm}
                            validationSchema={yup.object().shape({
                                name: yup.string().max(50).required(),
                                description: yup
                                    .string()
                                    .max(200)
                                    .required(),
                            })}
                            onSubmit={(fields) => {
                                setIsSaving(true);
                                setClipName(fields.name);
                                setClipDescription(fields.description);
                                const isAdmin = Helpers.isAdmin();
                                const isMarketplace = props.type === 'marketplace';
                                if (isAdmin || isMarketplace) {
                                    saveClip(
                                        {
                                            ...props.clip,
                                            title: fields.name,
                                            description: fields.description,
                                        },
                                        () => setIsSaving(false)
                                    );
                                } else {
                                    setIsPaymentPopupVisible(true);
                                }
                            }}
                            initialValues={{ name: '', description: '' }}>
                            <Row>
                                <Col md={3}>
                                    <Label for="name">Clip Title:</Label>
                                </Col>
                                <Col md={9} className="flex-container">
                                    <Field
                                        type="text"
                                        name="name"
                                        value={props.clip.title}
                                        placeholder="Enter Clip name"></Field>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={3}>
                                    <Label for="name">Clip Description:</Label>
                                </Col>
                                <Col md={9} className="flex-container">
                                    <Field
                                        className="textarea"
                                        type="textarea"
                                        name="description"
                                        value={props.clip.description}
                                        placeholder="Enter Clip description"></Field>
                                </Col>
                            </Row>
                        </Form>
                        <div className="buttons-panel">
                            <Button
                                color="secondary"
                                onClick={() => props.reset()}>
                                Discard
                            </Button>
                            <ButtonLoader
                                buttonText={buttonText()}
                                loaderButtonText={''}
                                disabled={false}
                                isLoading={isSaving}
                                onClick={() => modalForm.current.handleSubmit()}
                                className="btn btn-primary"></ButtonLoader>
                        </div>
                    </ModalBody>
                </Rodal>
            </div>
            {isPaymentPopupVisible ? (
                <SaveClipPaymentComponent
                    isVisible={isPaymentPopupVisible}
                    clip={props.clip}
                    clipName={clipName}
                    clipDescription={clipDescription}
                    setIsVisible={setIsPaymentPopupVisible}
                    conferenceId={props.clip.conferenceId}
                    paymentSuccess={paymentSuccess}
                    groupId={props.groupId}></SaveClipPaymentComponent>
            ) : (
                []
            )}
        </div>
    );
};
